import { Activity, memo, useState } from 'react';

import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import menuItems from 'menu-items';

import { useGetMenuMaster } from 'api/menu';
import useRBAC from 'hooks/useRBAC';

// ==============================|| SIDEBAR MENU LIST ||============================== //

function MenuList() {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { isSuperAdmin, isAdmin, isManager, can, canViewSystemConfig, canViewAuditLogs, canManageRoles } = useRBAC();

  const [selectedID, setSelectedID] = useState('');

  const lastItem = null;

  let lastItemIndex = menuItems.items.length - 1;
  let remItems = [];
  let lastItemId;

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const filterByRBAC = (item) => {
    // Security & Access control
    if (item.id === 'security-governance') return can('ACCESS_CONTROL:VIEW') || isSuperAdmin;
    if (item.id === 'security' || item.id === 'security-mfa' || item.id === 'security-group') return true;

    // API & Integrations
    if (item.id === 'api') return can('API_INTEGRATIONS:VIEW') || isSuperAdmin;

    // Audit logs
    if (item.id === 'audit-logs') return can('AUDIT_LOGS:VIEW') || canViewAuditLogs;

    // System configurations
    if (item.id === 'system-configs') return can('SYSTEM_CONFIG:VIEW') || canViewSystemConfig;

    // Client settings & versioning
    if (item.id === 'client-settings') return can('CLIENT_SETTINGS:VIEW') || isSuperAdmin || isAdmin;

    // Payments & Tariffs
    if (item.id === 'payment') return can('PAYMENT_RECORDS:VIEW') || can('PAYMENT_TARIFFS:VIEW') || isSuperAdmin || isAdmin;
    
    // Templates management
    if (item.id === 'device-email-templates') return can('TEMPLATES:VIEW') || isSuperAdmin || isAdmin;
    if (item.id === 'device-sms-templates') return can('TEMPLATES:VIEW') || isSuperAdmin || isAdmin;

    // Staff / Manager management
    if (item.id === 'user-manager') return can('STAFF:VIEW') || canManageRoles || isSuperAdmin;
    if (item.id === 'user-bind') return can('COURIERS:BIND_DEVICE') || isSuperAdmin || isAdmin;

    return true;
  };

  const filterTree = (items) => {
    return items.filter(filterByRBAC).map(item => {
      if (item.children) {
        return { ...item, children: filterTree(item.children) };
      }
      return item;
    }).filter(item => {
      if (item.children) {
        return item.children.length > 0;
      }
      return true;
    });
  };

  const filteredMenuItems = filterTree(menuItems.items.slice(0, lastItemIndex + 1));

  const navItems = filteredMenuItems.map((item, index) => {
    switch (item.type) {
      case 'group':
        const filteredItem = item;

        if (filteredItem.url && filteredItem.id !== lastItemId) {
          return (
            <List key={filteredItem.id}>
              <NavItem item={filteredItem} level={1} isParents setSelectedID={() => setSelectedID('')} />
              <Activity mode={index !== 0 ? 'visible' : 'hidden'}>
                <Divider sx={{ py: 0.5 }} />
              </Activity>
            </List>
          );
        }

        return (
          <Box key={filteredItem.id}>
            {index > 0 && (
              <Divider sx={{ my: 1, mx: 2, borderColor: 'rgba(226, 232, 240, 0.8)' }} />
            )}
            <NavGroup
              setSelectedID={setSelectedID}
              selectedID={selectedID}
              item={filteredItem}
              lastItem={lastItem}
              remItems={remItems}
              lastItemId={lastItemId}
            />
          </Box>
        );
      default:
        return (
          <Typography key={item.id} variant="h6" align="center" sx={{ color: 'error.main' }}>
            Menu Items Error
          </Typography>
        );
    }
  });

  return <Box {...(drawerOpen && { sx: { mt: 0.5 } })}>{navItems}</Box>;
}

export default memo(MenuList);
